"use client";

import { useEffect, useMemo, useRef } from "react";
import { ArrowLeft, MessageCircle, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { WhatsappConversation, WhatsappMessage } from "../types";
import { MessageBubble } from "./message-bubble";
import { MessageInput } from "./message-input";

type ChatWindowProps = {
  conversation?: WhatsappConversation | null;
  messages: WhatsappMessage[];
  isLoadingMessages?: boolean;
  isSending?: boolean;
  errorMessage?: string | null;
  containerClassName?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  canDeleteConversation?: boolean;
  isDeletingConversation?: boolean;
  onSendMessage: (message: string) => void | Promise<void>;
  onMarkAsRead: () => void | Promise<void>;
  onDeleteConversation?: () => void | Promise<void>;
};

export function ChatWindow({
  conversation,
  messages,
  isLoadingMessages,
  isSending,
  errorMessage,
  containerClassName,
  showBackButton,
  onBack,
  canDeleteConversation,
  isDeletingConversation,
  onSendMessage,
  onMarkAsRead,
  onDeleteConversation,
}: ChatWindowProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const lastMarkAsReadKeyRef = useRef<string>("");
  const safeMessages = useMemo(() => messages ?? [], [messages]);

  useEffect(() => {
    if (!conversation) {
      return;
    }
    if (!safeMessages.length) {
      return;
    }
    const target = scrollerRef.current;
    if (!target) {
      return;
    }
    target.scrollTop = target.scrollHeight;
  }, [conversation, safeMessages]);

  useEffect(() => {
    if (!conversation) {
      return;
    }

    const unreadCount = Number(conversation.unreadCount ?? 0);
    if (unreadCount <= 0) {
      lastMarkAsReadKeyRef.current = `${conversation.id}:0`;
      return;
    }

    const currentKey = `${conversation.id}:${unreadCount}`;
    if (lastMarkAsReadKeyRef.current === currentKey) {
      return;
    }

    lastMarkAsReadKeyRef.current = currentKey;
    void onMarkAsRead();
  }, [conversation, onMarkAsRead]);

  if (!conversation) {
    return (
      <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
        <div>
          <MessageCircle className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Selecione uma conversa</p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            As mensagens de clientes aparecem aqui em tempo real.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex h-full min-h-[460px] min-w-0 flex-col overflow-hidden rounded-[10px] border border-[#e5e7eb] bg-white dark:border-slate-700 dark:bg-slate-900",
        containerClassName,
      )}
    >
      <div className="flex min-w-0 items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <div className="flex min-w-0 items-center gap-2">
          {showBackButton ? (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          ) : null}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
              {conversation.contactName?.trim() || formatPhone(conversation.contactPhone)}
            </p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-300">
              {formatPhone(conversation.contactPhone)}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canDeleteConversation && onDeleteConversation ? (
            <button
              type="button"
              onClick={onDeleteConversation}
              disabled={isDeletingConversation}
              className="inline-flex h-8 items-center gap-1 rounded-lg border border-rose-200 px-2.5 text-[11px] font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-900/70 dark:text-rose-300 dark:hover:bg-rose-950"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {isDeletingConversation ? "Limpando..." : "Limpar da aplicacao"}
            </button>
          ) : null}
          <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {conversation.status || "open"}
          </span>
        </div>
      </div>

      <div ref={scrollerRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4 dark:bg-slate-950">
        {isLoadingMessages ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Carregando mensagens...</p>
        ) : errorMessage ? (
          <p className="text-sm text-rose-600">{errorMessage}</p>
        ) : !safeMessages.length ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Nenhuma mensagem nesta conversa.</p>
        ) : (
          safeMessages.map((message) => <MessageBubble key={message.id} message={message} />)
        )}
      </div>

      <MessageInput disabled={Boolean(errorMessage)} isSending={isSending} onSend={onSendMessage} />
    </div>
  );
}

function formatPhone(phone: string): string {
  const digits = String(phone ?? "").replace(/\D/g, "");
  if (digits.length < 12) {
    return phone;
  }
  const country = digits.slice(0, 2);
  const ddd = digits.slice(2, 4);
  const start = digits.slice(4, 9);
  const end = digits.slice(9);
  return `+${country} (${ddd}) ${start}-${end}`;
}
